import {
  RequestTimeOffResponse as Response,
  Schedule,
  TimeOffEvent,
  TimeOffRequestListQueryParams,
  convertTimeOffEvent,
  toDateString,
} from '@nextforce/api'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from 'app/i18l'
import { useServer } from 'app/server/context'
import { BaseHomeItem, HomeItem, SummaryItem } from 'home/types'
import {
  makeErrorHomeItem,
  makeSuccessHomeItem,
} from 'home/update_app/shared/utils'
import { groupBy } from 'lodash'
import moment from 'moment'
import { MINS_TO_MS } from 'shared/constants'
import { RetryQuery } from 'shared/queries/utils'
import { useStores } from 'stores/queries/list'
import url from 'url'
import { useUser } from 'users/queries/detail'
import keys from './shared/keys'

const timeOffRequestListQueryParams: TimeOffRequestListQueryParams = {
  role: 'ROLE_OBSERVER',
  startDate: toDateString(moment()),
}

export function useTimeOffRequestList<T = TimeOffEvent[]>(
  params?: TimeOffRequestListQueryParams | undefined,
  schedule?: Schedule | undefined,
  enabled = true,
  select?: (data: TimeOffEvent[]) => T,
  retryQuery?: RetryQuery
) {
  const { server } = useServer()
  const { data: user } = useUser()
  const { data: stores } = useStores(data => data.stores)
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: keys.list(schedule, params),
    queryFn: fetchTimeOffRequestList,
    enabled: !!user && !!stores && enabled,
    refetchInterval: 15 * MINS_TO_MS,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    select,
    retry: retryQuery ?? false,
  })

  if (!enabled) {
    queryClient.removeQueries({ queryKey: keys.list(schedule, params) })
  }

  return query

  async function fetchTimeOffRequestList() {
    const timeOffUrl = getUrl(schedule, params)
    const response = await server.get<Response.TimeOffEvent[]>(timeOffUrl)
    return response.map(event => convertTimeOffEvent(event))
  }
}

function getUrl(
  schedule: Schedule | undefined,
  params: TimeOffRequestListQueryParams | undefined
): string {
  const pathname = '/v2/time_off'
  const query: { [key: string]: string | boolean | number | undefined } = {}
  if (!schedule) {
    query.role = 'ROLE_OBSERVER'
    if (params?.storeId) {
      query.store_id = params.storeId
    }
    if (params?.startDate) {
      query.start_date = params?.startDate
    }
    if (params?.endDate) {
      query.end_date = params?.endDate
    }
  } else {
    if (schedule.calendar === 'store') {
      query.role = 'ROLE_OBSERVER'
      query.store_id = schedule.storeId
    } else {
      query.role = 'ROLE_HELPER'
    }
    if (params?.startDate) {
      query.start_date = params?.startDate
    }
    if (params?.endDate) {
      query.end_date = params?.endDate
    }
    if (params?.shiftDate) {
      query.shift_date = params?.shiftDate
    }
    if (!query.shift_date && !query.start_date) {
      query.shift_date = 'current'
    }
  }
  return url.format({
    pathname,
    query,
  })
}

export function useTimeOffRequestsHomeItem(): HomeItem | undefined {
  const { data: user } = useUser()
  const { data: stores } = useStores(data => data.stores)
  const { data: timeOffRequestsEnabled } = useStores(data =>
    data.stores?.some(
      store =>
        store.preferences?.showTimeOffRequest &&
        user?.permissions?.canApproveTimeOffRequests
    )
  )
  const {
    data: pendingTimeOffRequests,
    isError: isPendingTimeOffRequestsError,
    error: pendingTimeOffRequestsError,
  } = useTimeOffRequestList(
    timeOffRequestListQueryParams,
    undefined,
    timeOffRequestsEnabled,
    data => data.filter(timeOffRequest => timeOffRequest.status === 'PENDING')
  )

  const baseItem = {
    title: t('timeOffRequestList'),
    route: { name: 'time_off_request_list' },
    color: 'black',
    backgroundColor: '#FFBBB8',
    params: {},
  } as BaseHomeItem

  if (!timeOffRequestsEnabled || !pendingTimeOffRequests) {
    return
  }

  if (isPendingTimeOffRequestsError) {
    return makeErrorHomeItem(baseItem, pendingTimeOffRequestsError)
  }

  const groupedByStore = groupBy(
    pendingTimeOffRequests,
    timeOffRequest => timeOffRequest.storeId
  )

  const storeIds = Object.keys(groupedByStore)

  const summaryItems: SummaryItem[] =
    storeIds.map(key => {
      return {
        title: `Store ${
          stores?.find(store => store.id === key)?.storeNumber
        } - ${t('timeOffRequests', {
          count: groupedByStore[key].length,
        })}`,
        userList: groupedByStore[key].map(
          timeOffRequests => timeOffRequests.user
        ),
      }
    }) ?? []

  return makeSuccessHomeItem(baseItem, summaryItems)
}
